// File type configuration - easy to extend with new types (e.g., audio)
export type FileType = 'video' | 'document' | 'image';

type FileTypeConfig = {
  extensions: string[];
};

const FILE_TYPE_CONFIG: Record<Exclude<FileType, 'image'>, FileTypeConfig> = {
  video: {
    extensions: ['.mp4', '.mov', '.webm', '.avi', '.mkv', '.m4v', '.3gp'],
  },
  document: {
    extensions: ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.txt', '.csv'],
  },
};

// Get file type from URL
export const getFileType = (url: string): FileType => {
  const lowercaseUrl = url.toLowerCase();
  for (const [type, config] of Object.entries(FILE_TYPE_CONFIG)) {
    if (config.extensions.some((ext) => lowercaseUrl.endsWith(ext))) {
      return type as FileType;
    }
  }
  return 'image';
};

// Truncate long file names while preserving extension
export const truncateFileName = (fileName: string, maxLength: number = 50): string => {
  if (fileName.length <= maxLength) return fileName;

  const extension = fileName.split('.').pop() || '';
  const nameWithoutExt = fileName.substring(0, fileName.length - extension.length - 1);
  return `${nameWithoutExt.substring(0, maxLength - extension.length - 4)}....${extension}`;
};

// Get raw filename from URL (no truncation)
export const getRawFileName = (url: string): string => url.split('/').pop() || '';

// Get filename from URL (truncated for display)
export const getFileNameFromUrl = (url: string): string => truncateFileName(getRawFileName(url));

// Separate attachments by type
export const separateAttachments = (
  urls: string[],
): { imageUrls: string[]; videoUrls: string[]; documentUrls: string[] } => {
  const result = { imageUrls: [] as string[], videoUrls: [] as string[], documentUrls: [] as string[] };

  urls.forEach((url) => {
    const type = getFileType(url);
    if (type === 'video') result.videoUrls.push(url);
    else if (type === 'document') result.documentUrls.push(url);
    else result.imageUrls.push(url);
  });

  return result;
};
